import { Add, ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, Stack, styled, Typography } from '@mui/material';
import { orderBy } from 'lodash';
import { FC, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Container from 'src/components/atoms/Container/Container';
import { AnnouncementDialog } from 'src/components/organisms/AnnouncementDialog/AnnouncementDialog';
import { AnnouncementTimeline } from 'src/components/organisms/AnnouncementTimeline/AnnouncementTimeline';
import { AnnouncementRow } from 'src/domain/table/announcement.row';
import { useAnnouncements } from 'src/hooks/announcements/useAnnouncements';
import { useIsAdmin } from 'src/hooks/auth/useIsAdmin';
import { mapAnnouncements } from 'src/mapping/announcements.mapping';

export const AnnouncementSystem: FC = () => {
    const { data, refetch } = useAnnouncements();
    const isAdmin = useIsAdmin();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentAnnouncement, setCurrentAnnouncement] = useState<AnnouncementRow | undefined>();

    const announcements = mapAnnouncements(data);

    const activeAnnouncements = orderBy(
        announcements.filter((announcement) => !announcement.resolved),
        ['created'],
        ['desc']
    );
    const resolvedAnnouncements = orderBy(
        announcements.filter((announcement) => announcement.resolved),
        ['resolved'],
        ['desc']
    );

    const ref = useRef<HTMLDivElement>(null);

    const systemStatusType = useMemo(() => {
        if (announcements.some((announcement) => announcement.type === 'MajorIncident' && !announcement.resolved)) {
            return 'error';
        } else if (
            announcements.some((announcement) => announcement.type === 'MinorIncident' && !announcement.resolved)
        ) {
            return 'warning';
        }
    }, [announcements]);

    const systemStatusDate = useMemo(() => {
        if (systemStatusType === 'error') {
            const announcement = announcements.find(
                (announcement) => announcement.type === 'MajorIncident' && !announcement.resolved
            );

            return announcement?.created?.toLocaleTimeString([], {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } else if (systemStatusType === 'warning') {
            const announcement = announcements.find(
                (announcement) => announcement.type === 'MinorIncident' && !announcement.resolved
            );

            return announcement?.created?.toLocaleTimeString([], {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        }
    }, [announcements, systemStatusType]);

    const onAddAnnouncement = () => {
        setCurrentAnnouncement(undefined);
        setDialogOpen(true);
    };

    const handleEditAnnouncement = (announcement: AnnouncementRow) => {
        setCurrentAnnouncement({
            id: announcement.id,
            title: announcement.title,
            description: announcement.description,
            type: announcement.type,
            module: null,
            created: announcement.created,
            modified: announcement.modified,
            resolved: announcement.resolved,
            read: announcement.read,
        });
        setDialogOpen(true);
        ref.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const onCloseDialog = () => {
        setDialogOpen(false);
        setCurrentAnnouncement(undefined);
    };

    return (
        <>
            <AnnouncementDialog
                id={currentAnnouncement?.id}
                title={currentAnnouncement?.title}
                description={currentAnnouncement?.description}
                type={currentAnnouncement?.type}
                resolved={!!currentAnnouncement?.resolved}
                open={dialogOpen}
                handleCloseDialog={onCloseDialog}
                handleRefetch={refetch}
            />
            <Wrapper>
                <Box>
                    <Link
                        to={{
                            pathname: '/',
                            search: location.search,
                        }}
                    >
                        <Button variant="text" startIcon={<ArrowBack />}>
                            Back to front page
                        </Button>
                    </Link>
                </Box>
                <Alert severity={systemStatusType} ref={ref}>
                    {systemStatusType === 'error' && `Major incident(s) on ${systemStatusDate}`}
                    {systemStatusType === 'warning' && `Incident on ${systemStatusDate}`}
                    {!systemStatusType && 'All systems operational'}
                </Alert>
                <Container>
                    <TitleWrapper>
                        <Typography variant="h3">System Status</Typography>
                        {isAdmin && (
                            <Button variant="contained" color="info" startIcon={<Add />} onClick={onAddAnnouncement}>
                                Add announcement
                            </Button>
                        )}
                    </TitleWrapper>

                    <Stack
                        sx={{
                            mt: 5,
                            gap: 4,
                        }}
                    >
                        <Stack>
                            <Typography variant="subtitle1">Active announcements</Typography>
                            {activeAnnouncements.length ? (
                                <AnnouncementTimeline
                                    announcements={activeAnnouncements}
                                    handleEditAnnouncement={handleEditAnnouncement}
                                />
                            ) : (
                                <Typography variant="caption">No announcements found</Typography>
                            )}
                        </Stack>
                        <Stack>
                            <Typography variant="subtitle1">Resolved announcements</Typography>
                            {resolvedAnnouncements.length ? (
                                <AnnouncementTimeline
                                    announcements={resolvedAnnouncements}
                                    handleEditAnnouncement={handleEditAnnouncement}
                                />
                            ) : (
                                <Typography variant="caption">No announcements found</Typography>
                            )}
                        </Stack>
                    </Stack>
                </Container>
            </Wrapper>
        </>
    );
};

const Wrapper = styled(Stack)(({ theme }) => ({
    width: '70%',
    maxWidth: '1440px',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: theme.spacing(4),
}));

const TitleWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
}));
